import React from 'react'
import { useScrollReveal, useStaggeredReveal } from '../../../hooks/useScrollReveal.jsx'
import styles from './AboutSection.module.css'

const EXPERTISE_AREAS = [
  {
    title: 'AI & LLM Integration',
    description: 'Practical implementation of AI solutions, RAG pipelines, and intelligent automation that drives real business outcomes.'
  },
  {
    title: 'Multi-Tenant SaaS',
    description: 'Architecting scalable, secure multi-tenant platforms with Docker and Kubernetes that grow from startup to enterprise without rewrites.'
  },
  {
    title: 'Growth Engineering',
    description: 'Building product features and infrastructure that directly impact user acquisition, retention, and revenue.'
  }
]

const AboutSection = () => {
  const { ref: headlineRef, isVisible: headlineVisible } = useScrollReveal({ threshold: 0.2 })
  const { ref: contentRef, isVisible: contentVisible } = useScrollReveal({ threshold: 0.1, delay: 150 })
  const { containerRef: cardsRef, getItemProps } = useStaggeredReveal(EXPERTISE_AREAS.length, {
    staggerDelay: 150,
    baseDelay: 100
  })

  return (
    <section className={styles.about} id="about">
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.textContent}>
            <h2
              ref={headlineRef}
              className={styles.headline}
              style={{
                opacity: headlineVisible ? 1 : 0,
                transform: headlineVisible ? 'translateY(0)' : 'translateY(30px)',
                transition: 'opacity 0.6s ease, transform 0.6s ease'
              }}
            >
              <span className={styles.highlightNumber}>12 years</span> of shipping products that drive growth.
            </h2>

            <div
              ref={contentRef}
              style={{
                opacity: contentVisible ? 1 : 0,
                transform: contentVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s'
              }}
            >
              <p className={styles.description}>
                I started my career deep in <strong>PHP and Laravel</strong>, building e-commerce platforms and payment systems processing millions in transactions. As the industry evolved, I expanded into <strong>Node.js and TypeScript</strong>, architecting real-time systems and APIs at scale with <strong>Docker and Kubernetes</strong>.
              </p>

              <p className={styles.description}>
                Today, I specialize in <strong>AI/LLM integration</strong> and <strong>multi-tenant SaaS architecture</strong>. I help companies embed intelligent features into their products—from RAG pipelines to automated workflows—while ensuring the underlying platform can scale with their growth.
              </p>

              <p className={styles.description}>
                Based in <strong>Victoria, BC</strong>, I work remotely with teams worldwide. My focus is always the same: build technology that moves the needle on growth, not just checks technical boxes.
              </p>
            </div>

            <div className={styles.stats}>
              <div className={styles.statItem}>
                <span className={styles.statIcon}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                </span>
                <div className={styles.statText}>
                  <span className={styles.statLabel}>Victoria, BC</span>
                  <span className={styles.statSublabel}>(Remote)</span>
                </div>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statIcon}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                </span>
                <div className={styles.statText}>
                  <span className={styles.statLabel}>12+ Years</span>
                  <span className={styles.statSublabel}>Experience</span>
                </div>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statIcon}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                </span>
                <div className={styles.statText}>
                  <span className={styles.statLabel}>Principal</span>
                  <span className={styles.statSublabel}>Level</span>
                </div>
              </div>
            </div>
          </div>

          <div ref={cardsRef} className={styles.expertiseCards}>
            {EXPERTISE_AREAS.map((area, index) => (
              <div key={index} className={styles.expertiseCard} {...getItemProps(index)}>
                <h3 className={styles.expertiseTitle}>{area.title}</h3>
                <p className={styles.expertiseDescription}>{area.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default AboutSection
